import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { Observable, Subject, Subscription, debounceTime, finalize } from 'rxjs';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiDialogModule, OuiDialogService } from 'omnium-ui/dialog';
import { OuiSelectOption, OuiTextfield } from 'omnium-ui/form-field';
import { OuiSelectFilter } from 'omnium-ui/select-filter';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiTableModule } from 'omnium-ui/table';
import { ActionAuthorizationService, doublonsBusterActionCodes } from '../../../../core/auth/action-authorization.service';
import { ConsultantCardComponent } from '../../../../core/components/consultant-card/consultant-card.component';
import { BouquetServiceConsultantRow, BouquetServiceConsultantsPage } from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { ConsultantSubscriptionTagComponent } from '../../shared/consultant-subscription-tag/consultant-subscription-tag.component';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import { createFrenchPaginatorIntl } from '../shared/atlas-list-page.paginator';
import { OuiIconModule } from 'omnium-ui/icon';
import { OuiTooltipModule } from "omnium-ui/tooltip";
import {
  BouquetServiceResiliationDialogComponent,
  BouquetServiceResiliationDialogResult
} from './bouquet-service-resiliation-dialog/bouquet-service-resiliation-dialog.component';
import {
  BouquetServiceActivationDialogComponent,
  BouquetServiceActivationDialogResult
} from './bouquet-service-activation-dialog/bouquet-service-activation-dialog.component';

@Component({
  selector: 'dbw-atlas-bouquet-service-page',
  imports: [
    CommonModule,
    MatPaginatorModule,
    MatTableModule,
    OuiButtonModule,
    OuiDialogModule,
    OuiSelectFilter,
    OuiTableModule,
    OuiTextfield,
    ConsultantCardComponent,
    ConsultantSubscriptionTagComponent,
    StateCardComponent,
    OuiIconModule,
    OuiTooltipModule
],
  providers: [
    {
      provide: MatPaginatorIntl,
      useFactory: createFrenchPaginatorIntl,
    },
  ],
  templateUrl: './bouquet-service.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bouquet-service.component.scss',
})
export class AtlasBouquetServicePageComponent implements OnInit, OnDestroy {
  readonly displayedColumns = ['consultant', 'subscription', 'subscriptionEndDate', 'actions'];
  readonly pageSizeOptions = [10, 25, 50, 100];
  readonly consultantIdFilter = signal('');
  readonly consultantCodeFilter = signal('');
  readonly consultantNameFilter = signal('');
  readonly subscriptionStatusFilter = signal<string[]>([]);
  readonly subscriptionStatusOptions: OuiSelectOption<string>[] = [
    { label: 'Abonné', value: 'subscribed' },
    { label: "Désabonnement programmé", value: 'terminationScheduled' },
    { label: 'Non abonné', value: 'notSubscribed' },
  ];
  readonly loading = signal(true);
  readonly actionInProgressConsultantId = signal<number | undefined>(undefined);
  readonly error = signal<string | undefined>(undefined);
  readonly page = signal<BouquetServiceConsultantsPage | undefined>(undefined);
  readonly pageIndex = signal(0);
  readonly pageSize = signal(25);
  readonly canManageSubscription = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.consultantBouquetServiceSubscriptionManage));

  private readonly filterChanges = new Subject<void>();
  private loadSubscription?: Subscription;
  private actionSubscription?: Subscription;
  private dialogSubscription?: Subscription;
  private filterSubscription?: Subscription;

  constructor(
    private readonly apiService: DoublonsBusterApiService,
    private readonly actionAuthorizationService: ActionAuthorizationService,
    private readonly snackbarService: OuiSnackbarService,
    private readonly dialogService: OuiDialogService
  ) {}

  ngOnInit(): void {
    this.filterSubscription = this.filterChanges.pipe(debounceTime(300)).subscribe(() => {
      this.pageIndex.set(0);
      this.loadPage();
    });

    this.loadPage();
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.actionSubscription?.unsubscribe();
    this.dialogSubscription?.unsubscribe();
    this.filterSubscription?.unsubscribe();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.loadPage();
  }

  reload(): void {
    this.loadPage();
  }

  onConsultantIdFilterChange(value: string): void {
    this.consultantIdFilter.set(value);
    this.queueFilterReload();
  }

  onConsultantCodeFilterChange(value: string): void {
    this.consultantCodeFilter.set(value);
    this.queueFilterReload();
  }

  onConsultantNameFilterChange(value: string): void {
    this.consultantNameFilter.set(value);
    this.queueFilterReload();
  }

  onSubscriptionStatusFilterChange(value: string[]): void {
    this.subscriptionStatusFilter.set(value);
    this.queueFilterReload();
  }

  activateSubscription(row: BouquetServiceConsultantRow): void {
    if (this.actionInProgressConsultantId() !== undefined) {
      return;
    }

    this.dialogSubscription?.unsubscribe();
    this.dialogSubscription = this.dialogService
      .openDialog(
        BouquetServiceActivationDialogComponent,
        {
          consultantId: row.consultantId,
          consultantLabel: row.displayName || `Consultant #${row.consultantId}`,
          currentEndDate: row.subscriptionStatus === 'terminationScheduled' ? row.subscriptionEndDate : null,
        },
        undefined,
        '520px'
      )
      .afterClosed()
      .subscribe((result?: BouquetServiceActivationDialogResult) => {
        if (!result?.endDate) {
          return;
        }

        this.executeSubscriptionAction(
          row.consultantId,
          () => this.apiService.activateConsultantBouquetServiceSubscription(row.consultantId, result.endDate),
          'Abonnement Bouquet de service activé.');
      });
  }

  terminateSubscription(row: BouquetServiceConsultantRow): void {
    if (this.actionInProgressConsultantId() !== undefined) {
      return;
    }

    this.dialogSubscription?.unsubscribe();
    this.dialogSubscription = this.dialogService
      .openDialog(
        BouquetServiceResiliationDialogComponent,
        {
          consultantId: row.consultantId,
          consultantLabel: row.displayName || `Consultant #${row.consultantId}`,
          currentEndDate: row.subscriptionStatus === 'terminationScheduled' ? row.subscriptionEndDate : null,
        },
        undefined,
        '520px'
      )
      .afterClosed()
      .subscribe((result?: BouquetServiceResiliationDialogResult) => {
        if (!result?.terminationDate) {
          return;
        }

        this.executeSubscriptionAction(
          row.consultantId,
          () => this.apiService.terminateConsultantBouquetServiceSubscription(row.consultantId, result.terminationDate),
          'Abonnement Bouquet de service résilié.');
      });
  }

  getActivationTooltip(row: BouquetServiceConsultantRow): string {
    return row.subscriptionStatus === 'terminationScheduled' ? 'Annuler le désabonnement' : "Activer l'abonnement";
  }

  private executeSubscriptionAction(
    consultantId: number,
    request: () => Observable<{ message?: string | null }>,
    successMessage: string
  ): void {
    if (this.actionInProgressConsultantId() !== undefined) {
      return;
    }

    this.actionInProgressConsultantId.set(consultantId);
    this.actionSubscription?.unsubscribe();
    this.actionSubscription = request()
      .pipe(finalize(() => this.actionInProgressConsultantId.set(undefined)))
      .subscribe({
        next: result => {
          this.snackbarService.open(result.message || successMessage, 'success');
          this.loadPage();
        },
        error: error => {
          this.snackbarService.open(error?.error?.detail ?? "L'action sur l'abonnement a échoué.", 'error');
        },
      });
  }

  private loadPage(): void {
    this.loading.set(true);
    this.error.set(undefined);
    this.loadSubscription?.unsubscribe();

    this.loadSubscription = this.apiService
      .getBouquetServiceConsultantsPage(this.pageIndex(), this.pageSize(), {
        consultantId: this.parseOptionalNumber(this.consultantIdFilter()),
        consultantCode: this.consultantCodeFilter(),
        consultantName: this.consultantNameFilter(),
        subscriptionStatuses: this.parseSubscriptionStatusFilter(),
      })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: page => {
          this.page.set(page);
          this.pageIndex.set(page.pageIndex);
          this.pageSize.set(page.pageSize);
        },
        error: error => {
          this.page.set(undefined);
          this.error.set(error?.error?.detail ?? 'Impossible de charger le reporting Bouquet de service.');
        },
      });
  }

  private queueFilterReload(): void {
    this.filterChanges.next();
  }

  private parseOptionalNumber(value: string): number | null {
    const trimmed = value.trim();

    if (!trimmed) {
      return null;
    }

    const parsedValue = Number(trimmed);

    return Number.isInteger(parsedValue) && parsedValue >= 0 ? parsedValue : null;
  }

  private parseSubscriptionStatusFilter(): string[] {
    return this.subscriptionStatusFilter()
      .map(value => value.trim().toLowerCase())
      .flatMap(value => {
        switch (value) {
          case 'subscribed':
            return ['subscribed'];
          case 'terminationscheduled':
            return ['terminationScheduled'];
          case 'notsubscribed':
            return ['notSubscribed'];
          default:
            return [];
        }
      });
  }
}
